import clsx from 'clsx';
import Button from 'components/Button';
import Icon from 'components/Icon';
import Pill from 'components/Pill';
import SortableContainer from 'components/Sortable/SortableContainer';
import SortableHandle from 'components/Sortable/SortableHandle';
import SortableItem from 'components/Sortable/SortableItem';
import TooltipWrapper from 'components/Tooltip';
import Truncator from 'components/Truncator';
import {FC, useCallback, useState} from 'react';
import {useFormContext} from 'react-hook-form';
import {useBackend} from 'state/backend';
import {twMerge} from 'tailwind-merge';
import {c} from 'ttag';

import DocumentTypePills from '../../DocumentTypePills';
import {FavouriteTemplateFormFields} from '../../services';
import {FavouriteTemplatesEditBackendData} from '../../types';
import AddItemModal from '../AddItemModal';

const Section: FC = () => {
    const {
        data: {availableDocumentTypes},
    } = useBackend<FavouriteTemplatesEditBackendData>();

    const [isModalOpen, setModalOpen] = useState(false);

    const {setValue, watch} = useFormContext<FavouriteTemplateFormFields>();
    const elements = watch('favouriteTemplates');

    const items = elements;
    const numberItems = items.length;

    const onItemRemove = useCallback(
        (index: number) => {
            const newItems = [...items];
            newItems.splice(index, 1);
            setValue(
                'favouriteTemplates',
                newItems.map((element) => element),
            );
        },
        [items, setValue],
    );

    return (
        <div className="my-4 flex min-w-[48rem] flex-col gap-y-3 text-base">
            <div className="overflow-hidden rounded border border-grey-300">
                <div>
                    <div className="flex flex-row items-start border-b border-grey-300 bg-grey-200 font-semibold">
                        <div className="w-20 p-2 pl-4">{c('component')
                            .t`Order`}</div>
                        <div className="w-56 p-2">
                            {c('component').t`Document Type(s)`}
                        </div>
                        <div className="flex-1 p-2">
                            {c('component').t`Template Name`}
                        </div>
                    </div>
                </div>
                <SortableContainer
                    items={items}
                    onSortChange={(newItems) =>
                        setValue(
                            'favouriteTemplates',
                            newItems.map((element) => element),
                        )
                    }
                    renderItem={(element, index) => (
                        <SortableItem
                            className={twMerge(
                                'flex border-grey-300 border-b',
                                index === numberItems - 1 &&
                                    'border-b-transparent',
                                clsx(element.isArchived && 'bg-grey-200'),
                            )}
                            id={element.id}
                            key={element.id}
                        >
                            <SortableHandle
                                className="w-20 p-2 pl-4"
                                id={element.id}
                            >
                                <Icon
                                    className="py-1"
                                    icon="slider_handle"
                                    size="base"
                                />
                            </SortableHandle>
                            <div className="w-56 p-2">
                                <DocumentTypePills
                                    availableDocumentTypes={
                                        availableDocumentTypes
                                    }
                                    documentTypeDisplayNames={
                                        element.documentTypeDisplayNames
                                    }
                                />
                            </div>
                            <div
                                className="flex flex-1 p-2 pl-4"
                                role="presentation"
                            >
                                <Truncator
                                    className={clsx(
                                        element.isArchived && 'opacity-40',
                                        'max-w-md',
                                    )}
                                    text={element.templateDisplayName}
                                />
                                {element.isArchived && (
                                    <TooltipWrapper
                                        message={c('component')
                                            .t`Archived Template, will not appear in Document Template picker`}
                                    >
                                        <Pill
                                            className="ml-2"
                                            color="grey_dark"
                                            label={c('component').t`Archived`}
                                        />
                                    </TooltipWrapper>
                                )}
                            </div>
                            <div className="mr-2 flex items-center">
                                <Button
                                    className="p-2"
                                    icon="trash"
                                    id={`delete-${element.id}`}
                                    kind="destructive"
                                    onClick={() => onItemRemove(index)}
                                    size="xs"
                                />
                            </div>
                        </SortableItem>
                    )}
                />
                {items.length === 0 && (
                    <div className="w-full p-2 text-grey-600">
                        {c('component').t`No Favorite Templates set.`}
                    </div>
                )}
            </div>
            <div className="flex gap-x-2">
                <Button
                    icon="add"
                    kind="secondary"
                    onClick={() => setModalOpen(true)}
                    size="sm"
                >
                    {c('component').t`Select Template(s)`}
                </Button>
                <AddItemModal
                    isOpen={isModalOpen}
                    onClose={() => setModalOpen(false)}
                />
            </div>
        </div>
    );
};

export default Section;
